import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Calendar,
  ListTodo,
  FileText,
  Video,
  TicketCheck,
  FileSpreadsheet,
  ShieldCheck,
  Bell,
  FolderKanban,
  Zap,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { Card } from '@/components/common/Card'
import { Badge } from '@/components/common/Badge'
import { apiClient } from '@/services/api'
import { getSocket } from '@/services/socket'
import { cn } from '@/lib/utils'

// --- Types ---

interface QuickAction {
  label: string
  type: 'markDone' | 'approve' | 'markRead' | 'startProgress'
  endpoint: string
  method: 'PATCH' | 'POST'
  payload: Record<string, unknown>
}

interface PriorityItem {
  id: string
  type: 'task' | 'content' | 'event' | 'meeting' | 'ticket' | 'quote' | 'approval' | 'notification' | 'project'
  title: string
  subtitle?: string
  score: number
  band: 'critico' | 'importante' | 'normale' | 'secondario'
  priority?: string
  status: string
  dueAt?: string
  startAt?: string
  endAt?: string
  clientName?: string
  projectName?: string
  actionable: boolean
  quickAction?: QuickAction
  navigateTo: string
}

interface TodayData {
  greeting: string
  date: string
  timeline: PriorityItem[]
  bands: {
    critico: PriorityItem[]
    importante: PriorityItem[]
    normale: PriorityItem[]
    secondario: PriorityItem[]
  }
  progress: {
    completed: number
    total: number
    percentage: number
  }
}

// --- Icons per tipo ---

const TYPE_ICONS: Record<string, typeof ListTodo> = {
  task: ListTodo,
  content: FileText,
  event: Calendar,
  meeting: Video,
  ticket: TicketCheck,
  quote: FileSpreadsheet,
  approval: ShieldCheck,
  notification: Bell,
  project: FolderKanban,
}

// --- Band config ---

const BAND_CONFIG = {
  critico: {
    title: 'Da fare subito',
    color: 'text-danger-600',
    dot: 'bg-danger-500',
    badge: 'danger' as const,
  },
  importante: {
    title: 'Priorita alta',
    color: 'text-warning-600',
    dot: 'bg-warning-500',
    badge: 'warning' as const,
  },
  normale: {
    title: 'Da completare oggi',
    color: 'text-primary-600',
    dot: 'bg-primary-500',
    badge: 'info' as const,
  },
  secondario: {
    title: 'Secondario',
    color: 'text-slate-500',
    dot: 'bg-slate-400',
    badge: 'neutral' as const,
  },
}

// --- Helpers ---

function formatTime(isoStr: string): string {
  return new Date(isoStr).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

function formatDueLabel(dueAt: string): { label: string; variant: 'danger' | 'warning' | 'neutral' } {
  const now = new Date()
  const due = new Date(dueAt)
  const diffH = (due.getTime() - now.getTime()) / 3600000

  if (diffH < -24) return { label: `Scaduto ${Math.abs(Math.floor(diffH / 24))}g fa`, variant: 'danger' }
  if (diffH < 0) return { label: 'Scaduto oggi', variant: 'danger' }
  if (diffH <= 2) return { label: `Tra ${Math.max(1, Math.floor(diffH * 60))} min`, variant: 'danger' }
  if (diffH <= 12) return { label: `Oggi ${formatTime(dueAt)}`, variant: 'warning' }
  if (diffH <= 24) return { label: 'Domani', variant: 'neutral' }
  return { label: new Date(dueAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }), variant: 'neutral' }
}

// --- Components ---

function ProgressHeader({ greeting, progress }: { greeting: string; progress: TodayData['progress'] }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{greeting}</h2>
      <div className="flex items-center gap-3">
        <div className="hidden sm:flex items-center gap-2">
          <div className="w-24 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-success-500 rounded-full transition-all duration-500"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap tabular-nums">
            {progress.completed}/{progress.total}
          </span>
        </div>
        <Badge variant={progress.percentage >= 75 ? 'success' : progress.percentage >= 40 ? 'info' : 'neutral'} size="sm">
          {progress.percentage}%
        </Badge>
      </div>
    </div>
  )
}

function TimelineStrip({ items }: { items: PriorityItem[] }) {
  if (items.length === 0) return null

  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
        Timeline Oggi
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {items.map((item) => {
          const now = new Date()
          const start = item.startAt ? new Date(item.startAt) : null
          const end = item.endAt ? new Date(item.endAt) : null
          const isNow = start && end && now >= start && now <= end
          const isPast = end && now > end

          return (
            <div
              key={item.id}
              className={cn(
                'flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors',
                isNow
                  ? 'bg-success-50 border-success-200 text-success-700'
                  : isPast
                    ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-slate-300'
              )}
            >
              {isNow && <Zap className="w-3.5 h-3.5 text-success-600" />}
              <span className="font-medium text-xs tabular-nums">
                {item.startAt ? formatTime(item.startAt) : ''}
              </span>
              <span className="truncate max-w-[140px]">{item.title}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function PriorityItemRow({
  item,
  onQuickAction,
  isActioning,
}: {
  item: PriorityItem
  onQuickAction: (item: PriorityItem) => void
  isActioning: boolean
}) {
  const navigate = useNavigate()
  const Icon = TYPE_ICONS[item.type] ?? ListTodo

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer group"
      onClick={() => navigate(item.navigateTo)}
    >
      <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold leading-snug text-slate-900 dark:text-slate-100 break-words">{item.title}</p>
        {item.subtitle && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{item.subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {item.dueAt && (
          <Badge variant={formatDueLabel(item.dueAt).variant} size="sm">
            {formatDueLabel(item.dueAt).label}
          </Badge>
        )}
        {item.quickAction && (
          <button
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-lg transition-colors',
              'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100',
              item.quickAction.type === 'approve'
                ? 'bg-success-50 text-success-600 hover:bg-success-100'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50',
            )}
            disabled={isActioning}
            onClick={(e) => {
              e.stopPropagation()
              onQuickAction(item)
            }}
          >
            {isActioning ? '...' : item.quickAction.label}
          </button>
        )}
      </div>
    </div>
  )
}

function UrgencyBand({
  band,
  items,
  onQuickAction,
  actioningId,
  defaultCollapsed = false,
}: {
  band: keyof typeof BAND_CONFIG
  items: PriorityItem[]
  onQuickAction: (item: PriorityItem) => void
  actioningId: string | null
  defaultCollapsed?: boolean
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const config = BAND_CONFIG[band]
  const maxVisible = 5
  const [showAll, setShowAll] = useState(false)
  const visibleItems = showAll ? items : items.slice(0, maxVisible)

  if (items.length === 0) return null

  return (
    <div className="rounded-2xl border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 shadow-card overflow-hidden">
      <button
        className="flex items-center gap-2 w-full px-4 py-3 text-left"
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
        <div className={cn('w-2 h-2 rounded-full', config.dot)} />
        <span className={cn('text-sm font-semibold', config.color)}>
          {config.title}
        </span>
        <Badge variant={config.badge} size="sm">
          {items.length}
        </Badge>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2">
          {visibleItems.map((item) => (
            <PriorityItemRow
              key={item.id}
              item={item}
              onQuickAction={onQuickAction}
              isActioning={actioningId === item.id}
            />
          ))}
          {!showAll && items.length > maxVisible && (
            <button
              className="w-full text-center text-xs text-slate-500 dark:text-slate-400 py-2 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                setShowAll(true)
              }}
            >
              e altri {items.length - maxVisible} elementi...
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// --- Main Component ---

export function TodayPriorities() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery<TodayData>({
    queryKey: ['dashboard', 'today'],
    queryFn: async () => {
      const res = await apiClient.get<any>('/dashboard/today')
      return res as TodayData
    },
    refetchInterval: 60_000,
    retry: 1,
  })

  // Socket real-time
  useEffect(() => {
    const socket = getSocket()
    if (!socket) return

    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] })
    }
    socket.on('data:changed', handler)
    return () => { socket.off('data:changed', handler) }
  }, [queryClient])

  // Quick action mutation
  const [actioningId, setActioningId] = useState<string | null>(null)
  const quickActionMutation = useMutation({
    mutationFn: async (item: PriorityItem) => {
      if (!item.quickAction) return
      setActioningId(item.id)
      const { endpoint, method, payload } = item.quickAction
      if (method === 'PATCH') await apiClient.patch(endpoint, payload)
      else await apiClient.post(endpoint, payload)
    },
    onSuccess: () => {
      setActioningId(null)
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: () => {
      setActioningId(null)
    },
  })

  const handleQuickAction = useCallback(
    (item: PriorityItem) => quickActionMutation.mutate(item),
    [quickActionMutation]
  )

  // Loading
  if (isLoading && !data) {
    return (
      <Card padding="md">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
          <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded w-full" />
          <div className="h-12 bg-slate-100 dark:bg-slate-700 rounded" />
          <div className="h-12 bg-slate-100 dark:bg-slate-700 rounded" />
        </div>
      </Card>
    )
  }

  if (!data) return null

  const hasSomething =
    data.timeline.length > 0 ||
    data.bands.critico.length > 0 ||
    data.bands.importante.length > 0 ||
    data.bands.normale.length > 0 ||
    data.bands.secondario.length > 0

  return (
    <Card padding="md">
      <ProgressHeader greeting={data.greeting} progress={data.progress} />

      {!hasSomething ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-success-500 mb-3" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Tutto in ordine per oggi!
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            Nessuna priorita urgente.
          </p>
        </div>
      ) : (
        <div className="space-y-3 mt-3">
          <TimelineStrip items={data.timeline} />

          <UrgencyBand
            band="critico"
            items={data.bands.critico}
            onQuickAction={handleQuickAction}
            actioningId={actioningId}
          />
          <UrgencyBand
            band="importante"
            items={data.bands.importante}
            onQuickAction={handleQuickAction}
            actioningId={actioningId}
          />
          <UrgencyBand
            band="normale"
            items={data.bands.normale}
            onQuickAction={handleQuickAction}
            actioningId={actioningId}
          />
          <UrgencyBand
            band="secondario"
            items={data.bands.secondario}
            onQuickAction={handleQuickAction}
            actioningId={actioningId}
            defaultCollapsed
          />
        </div>
      )}
    </Card>
  )
}
